/**
 * ConnectionStatus - Shows network connection status and auto-retry indicator
 * Appears when network issues are detected
 */
import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, CheckCircle } from 'lucide-react';

const ConnectionStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRecovering, setIsRecovering] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setIsRecovering(true);
      
      // Show recovering state briefly, then success
      setTimeout(() => {
        setIsRecovering(false);
        setShowSuccess(true);
        
        // Hide success after 2 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 2000);
      }, 1000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowSuccess(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Don't show anything when everything is fine
  if (isOnline && !isRecovering && !showSuccess) {
    return null;
  }

  return (
    <div className="fixed bottom-20 md:bottom-4 left-1/2 transform -translate-x-1/2 z-50">
      {!isOnline && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full shadow-lg animate-pulse">
          <WifiOff className="w-4 h-4" />
          <span className="text-sm font-medium">No connection - retrying...</span>
        </div>
      )}
      
      {isOnline && isRecovering && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-full shadow-lg">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">Reconnecting...</span>
        </div>
      )}
      
      {isOnline && showSuccess && !isRecovering && (
        <div className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-full shadow-lg">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Connection restored</span>
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;
